import * as joint from '@joint/core';
import { getFlowTypeInfo } from './constants';
import { EntityGroup, ThirdParty, BranchThirdParty } from './entity-nodes';
import {
    Callout,
    DrawnLine,
    DrawnRectangle,
    GroupSelection,
    IconElement,
    TextBox,
} from './drawable-elements';
import { Node } from './node-base';
import { AvailableIconNames, ConnectorStyle, FlowType } from './types';

// Shared editor, inspection, and mutation policy for editable properties, agent geometry,
// literal-flow endpoints, and non-obvious property couplings.
//
// Manual drag-tool eligibility remains a pointer-gesture concern, while economic-step domain
// eligibility remains owned by step handlers.

// Agent-facing cell kinds. Serialized cell types (flat class names) normalize onto these.
export type SnapshotCellKind =
    | 'entity'
    | 'entityGroup'
    | 'thirdParty'
    | 'textBox'
    | 'callout'
    | 'rectangle'
    | 'icon'
    | 'line'
    | 'flow'
    | 'unknown';

// Closed property vocabulary shared by inspection, editor aliases, and mutation policy.
export const ValueCapabilityIds = [
    'primaryLabel',
    'secondaryLabel',
    'text',
    'backgroundColor',
    'textColor',
    'borderColor',
    'shapeColor',
    'iconName',
    'strokeColor',
    'strokeWidth',
    'connectorStyle',
    'textLabels',
    'iconLabels',
] as const;

export const GeometryCapabilityIds = ['position', 'size', 'tailTip', 'sourcePoint', 'targetPoint'] as const;

export interface CellKindCapability {
    // Concept A only: can a literal flow arrow attach here? (Node | TextBox | IconElement.)
    // Named "literal" so it cannot be confused with manual drag eligibility (concept B) or
    // economic-step domain eligibility (concept C).
    isLiteralFlowEndpoint: boolean;
    editableProperties: string[];
    // Callouts support tailTip; a drawn line uses point endpoints rather than position/size.
    // Entity-like nodes expose position but not size: they are sized by the engine, while an
    // explicit position is an author's placement that opts the shape out of auto-layout.
    // A listed id is what the engine accepts, which is not always what a caller supplies: a
    // callout's tailTip is resolved from a target cell rather than passed in (see its coupling),
    // and the write path still validates the resolved value against this list.
    geometryProperties: string[];
    allowedValues?: Record<string, readonly string[]>;
    couplings?: string[];
}

const entityLikeProperties = ['primaryLabel', 'secondaryLabel', 'backgroundColor', 'textColor'];

// Position without size: placing an entity, group, or Third Party is authoring, sizing it is not.
const entityLikeGeometry = ['position'];
const entityLikeGeometryCoupling =
    'size is engine-owned; an explicit position opts the shape out of automatic layout';

const capabilityTable: Record<SnapshotCellKind, CellKindCapability> = {
    entity: {
        isLiteralFlowEndpoint: true,
        editableProperties: [...entityLikeProperties],
        geometryProperties: [...entityLikeGeometry],
        couplings: [entityLikeGeometryCoupling],
    },
    entityGroup: {
        isLiteralFlowEndpoint: true,
        editableProperties: [...entityLikeProperties],
        geometryProperties: [...entityLikeGeometry],
        couplings: [entityLikeGeometryCoupling],
    },
    thirdParty: {
        isLiteralFlowEndpoint: true,
        editableProperties: [...entityLikeProperties],
        geometryProperties: [...entityLikeGeometry],
        couplings: [entityLikeGeometryCoupling],
    },
    textBox: {
        isLiteralFlowEndpoint: true,
        editableProperties: ['text', 'backgroundColor', 'textColor', 'borderColor'],
        geometryProperties: ['position', 'size'],
    },
    callout: {
        isLiteralFlowEndpoint: false,
        editableProperties: ['text', 'backgroundColor', 'textColor'],
        geometryProperties: ['position', 'size', 'tailTip'],
        couplings: [
            'borderColor follows backgroundColor and is not separately editable',
            'tailTip is not set directly: aim the tail with tailTargetCellId, naming the cell the callout describes, and it is placed on the midpoint of that cell\'s facing side',
        ],
    },
    rectangle: {
        isLiteralFlowEndpoint: false,
        editableProperties: ['borderColor'],
        geometryProperties: ['position', 'size'],
    },
    icon: {
        isLiteralFlowEndpoint: true,
        editableProperties: ['iconName', 'shapeColor'],
        geometryProperties: ['position', 'size'],
        allowedValues: { iconName: AvailableIconNames },
    },
    line: {
        isLiteralFlowEndpoint: false,
        editableProperties: ['textLabels', 'iconLabels', 'strokeColor', 'strokeWidth'],
        geometryProperties: ['sourcePoint', 'targetPoint'],
        allowedValues: { iconLabels: AvailableIconNames },
    },
    flow: {
        isLiteralFlowEndpoint: false,
        editableProperties: ['textLabels', 'iconLabels', 'connectorStyle'],
        geometryProperties: [],
        allowedValues: {
            connectorStyle: [ConnectorStyle.Sharp, ConnectorStyle.Rounded, ConnectorStyle.Curved],
            iconLabels: AvailableIconNames,
        },
        couplings: ['color is determined by flowType and is not editable'],
    },
    unknown: {
        isLiteralFlowEndpoint: false,
        editableProperties: [],
        geometryProperties: [],
    },
};

const entityCellTypes = new Set(['CorporationCFC', 'Branch', 'Partnership', 'HybridEntity', 'ReverseHybrid', 'Disregarded']);
const thirdPartyCellTypes = new Set(['ThirdParty', 'BranchThirdParty']);
const flowLinkCellTypes = new Set([
    'PhysicalLink', 'LegalLink', 'PaymentLink', 'ServiceLink', 'IPTransferLink',
    'IPLicenseLink', 'AgreementLink', 'MiscellaneousLink', 'OwnershipLink',
    'standard.Link', // legacy, pre-typed graphs
]);

export function classifyCellType(serializedType: string): SnapshotCellKind {
    if (entityCellTypes.has(serializedType)) return 'entity';
    if (thirdPartyCellTypes.has(serializedType)) return 'thirdParty';
    if (serializedType === 'EntityGroup') return 'entityGroup';
    if (serializedType === 'TextBox') return 'textBox';
    if (serializedType === 'Callout') return 'callout';
    if (serializedType === 'DrawnRectangle') return 'rectangle';
    if (serializedType === 'IconElement') return 'icon';
    if (serializedType === 'DrawnLine') return 'line';
    if (flowLinkCellTypes.has(serializedType)) return 'flow';
    return 'unknown';
}

// instanceof-based classification for live editor cells; subclass-safe where the type-string
// switch would not be (a hypothetical Node subclass still classifies by its base).
export function classifyCell(cell: joint.dia.Cell): SnapshotCellKind {
    if (cell instanceof EntityGroup) return 'entityGroup';
    if (cell instanceof ThirdParty || cell instanceof BranchThirdParty) return 'thirdParty';
    if (cell instanceof Node) return 'entity';
    if (cell instanceof GroupSelection) return 'unknown'; // transient selection chrome, never persisted
    if (cell instanceof TextBox) return 'textBox';
    if (cell instanceof Callout) return 'callout';
    if (cell instanceof DrawnRectangle) return 'rectangle';
    if (cell instanceof IconElement) return 'icon';
    if (cell instanceof DrawnLine) return 'line';
    if (cell instanceof joint.shapes.standard.Link) return 'flow';
    return classifyCellType(String(cell.get('type') ?? ''));
}

export function buildCapabilityTable(): Record<SnapshotCellKind, CellKindCapability> {
    return capabilityTable;
}

export function supportsProperty(kind: SnapshotCellKind, capabilityId: string): boolean {
    const capability = capabilityTable[kind];
    return capability.editableProperties.includes(capabilityId)
        || capability.geometryProperties.includes(capabilityId);
}

export function isAllowedValue(kind: SnapshotCellKind, capabilityId: string, value: string): boolean {
    const allowedValues = capabilityTable[kind].allowedValues?.[capabilityId];
    return allowedValues === undefined || allowedValues.includes(value);
}

// Maps editor aliases onto canonical capability ids. TextBox/Callout content arrives as
// primaryLabel, while grouped DrawnLine stroke editing arrives as borderColor.
const uiPropertyAliases: Partial<Record<SnapshotCellKind, Record<string, string>>> = {
    textBox: { primaryLabel: 'text' },
    callout: { primaryLabel: 'text' },
    line: { borderColor: 'strokeColor' },
};

export function toCapabilityId(kind: SnapshotCellKind, uiProperty: string): string | null {
    const aliased = uiPropertyAliases[kind]?.[uiProperty] ?? uiProperty;
    return supportsProperty(kind, aliased) ? aliased : null;
}

export function isLiteralFlowEndpoint(cell: joint.dia.Cell): boolean {
    return capabilityTable[classifyCell(cell)].isLiteralFlowEndpoint;
}

// The kinds automatic layout places, and therefore the kinds an explicit position opts out of it.
// Annotations are always placed by their author and never carry the agentAdded hint.
export function isAutoLayoutPlaced(kind: SnapshotCellKind): boolean {
    return kind === 'entity' || kind === 'entityGroup' || kind === 'thirdParty';
}

// Reverse lookup: serialized link class name → FlowType enum *name* ("PaymentLink" → "Payment").
// Derived from the same getFlowTypeInfo metadata the forward path uses, so the two can never
// disagree; legacy 'standard.Link' and unknown classes return null.
const flowTypeNameByLinkClass: ReadonlyMap<string, string> = (() => {
    const map = new Map<string, string>();
    for (const value of Object.values(FlowType)) {
        if (typeof value !== 'number') {
            continue;
        }
        map.set(getFlowTypeInfo(value).linkClassName, FlowType[value]);
    }
    return map;
})();

export function flowTypeNameForLinkClass(serializedType: string): string | null {
    return flowTypeNameByLinkClass.get(serializedType) ?? null;
}
